if (!customElements.get("app-proxy-form")) {
  customElements.define(
    "app-proxy-form",
    class AppProxyForm extends HTMLElement {
      constructor() {
        super();

        // API response data (contains success flag and recipient data)
        this.verificationData = null;
        this.isManualEntry = false;
        this.isExistingRecord = false; // Track if data came from existing DB record

        // API configuration - use relative URLs for same-domain API calls
        this.apiBaseUrl = "/apps/recipients";
        // Get the current shop domain from the page
        this.shopDomain = window.Shopify?.shop || window.location.hostname;

        // DIN validation regex - basic format check only
        this.dinRegex = /^\d{2}[A-Z]\d{4}$/i;

        // Google Places Autocomplete
        this.autocomplete = null;
        this.facilityAutocomplete = null;
        this.GOOGLE_API_KEY = "AIzaSyCmdANF-KZmwQtVGBFem94DlQ_97bByGV0";

        // API Response Cache (in-memory only, per session)
        this.apiCache = new Map(); // Key: DIN (uppercase), Value: {response, timestamp}
      }

      connectedCallback() {
        // Initialize the component when it's added to the DOM
        this.initializeListeners();
      }

      initializeListeners() {
        // Get DOM elements
        this.wrapper = this.querySelector(".exat-attributes-wrap");
        this.dinInput = this.querySelector("#din_attribute");
        this.verifyBtn = this.querySelector("#inmate_search");
        this.additionalFields = this.querySelector("#additional_fields");
        this.inmateNameInput = this.querySelector("#fullname_attribute");
        this.facilityInput = this.querySelector("#facility_attribute");
        this.facilityAddressInput = this.querySelector(
          "#facility_address_attribute"
        );
        this.cityInput = this.querySelector("#city_attribute");
        this.stateInput = this.querySelector("#state_attribute");
        this.zipInput = this.querySelector("#zip_attribute");
        this.countryInput = this.querySelector("#country_attribute");
        this.isManualInput = this.querySelector("#is_manual_input");
        this.enableManualBtn = this.querySelector("#enable_manual");
        this.enableManualText = this.querySelector("#enable_manual_text");
        this.manualEntryControls = this.querySelector("#manual_entry_controls");

        // Alert elements
        this.dinAlert = this.querySelector("#din_alert");
        this.dinInputAlert = this.querySelector("#din_input_alert");
        this.notInCustodyAlert = this.querySelector("#not_incustody_alert");
        this.verifyWarning = this.querySelector("#verify_warning");
        this.recordUpdateError = this.querySelector("#record_update_error");
        this.dataIncorrectMessage = this.querySelector(
          "#data_incorrect_message"
        );
        this.verificationRequiredError = this.querySelector(
          "#verification_required_error"
        );

        // Field-specific error elements
        this.fullnameError = this.querySelector("#fullname_error");
        this.facilityError = this.querySelector("#facility_error");
        this.addressError = this.querySelector("#address_error");
        this.cityError = this.querySelector("#city_error");
        this.stateError = this.querySelector("#state_error");
        this.zipError = this.querySelector("#zip_error");

        // Add event listeners
        if (this.verifyBtn) {
          this.verifyBtn.addEventListener(
            "click",
            this.handleVerifyClick.bind(this)
          );
        }

        if (this.enableManualBtn) {
          this.enableManualBtn.addEventListener(
            "click",
            this.handleManualEntry.bind(this)
          );
          // Manual entry controls are hidden initially via CSS
        }

        // Add DIN change listener to reset verification
        if (this.dinInput) {
          this.dinInput.addEventListener(
            "input",
            this.handleDINChange.bind(this)
          );
        }

        // Add input listeners for real-time error clearing
        if (this.inmateNameInput) {
          this.inmateNameInput.addEventListener(
            "input",
            this.handleNameInput.bind(this)
          );
        }

        if (this.facilityInput) {
          this.facilityInput.addEventListener(
            "input",
            this.handleFacilityInputChange.bind(this)
          );
        }

        if (this.facilityAddressInput) {
          this.facilityAddressInput.addEventListener(
            "input",
            this.handleAddressInput.bind(this)
          );
        }

        if (this.cityInput) {
          this.cityInput.addEventListener(
            "input",
            this.handleCityInput.bind(this)
          );
        }

        if (this.stateInput) {
          this.stateInput.addEventListener(
            "input",
            this.handleStateInput.bind(this)
          );
        }

        if (this.zipInput) {
          this.zipInput.addEventListener(
            "input",
            this.handleZipInput.bind(this)
          );
        }

        // Initialize checkout protection
        this.initializeCheckoutProtection();
      }

      async handleVerifyClick() {
        // Clear any previous errors
        this.hideAllErrors();

        // Basic DIN format validation
        if (
          !this.dinInput.value ||
          !this.dinRegex.test(this.dinInput.value.toUpperCase())
        ) {
          this.showDINError();
          this.dinInput.focus();
          return;
        }

        const din = this.dinInput.value.toUpperCase();

        try {
          // Show loading state
          this.verifyBtn.disabled = true;
          this.verifyBtn.textContent = "Verifying...";

          // Check cache first
          const cachedResponse = this.getCachedResponse(din);
          if (cachedResponse) {
            // Use cached response
            this.verificationData = cachedResponse;

            if (cachedResponse.success) {
              this.populateFields(cachedResponse.recipient);
              this.showSuccessState();
            } else {
              this.showNotFoundState(cachedResponse.error);
            }
            return;
          }

          // Make API call if not cached
          const response = await fetch(
            `${this.apiBaseUrl}/lookup?din=${din}&shop=${this.shopDomain}`
          );

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const data = await response.json();

          // Cache the response
          this.setCachedResponse(din, data);

          if (data.success) {
            // Verification successful
            this.verificationData = data;
            this.populateFields(data.recipient);
            this.showSuccessState();
          } else {
            this.verificationData = data;
            this.showNotFoundState(data.error);
          }
        } catch (error) {
          this.showAPIError();
        } finally {
          this.verifyBtn.disabled = false;
          this.verifyBtn.textContent = "Verify";
        }
      }

      handleManualEntry() {
        // Call the auto-open logic (DRY principle)
        this.autoOpenManualEntry();
      }

      autoOpenManualEntry() {
        // Validation check - same as handleManualEntry
        if (!this.verificationData) {
          this.showVerificationRequiredError();
          return;
        }

        if (this.verificationData && this.verificationData.success) {
          this.showRecordUpdateError();
          return;
        }

        // Remove any lingering error styling
        this.hideAllErrors();

        // Show the informational message (not error)
        this.dinAlert.classList.remove("display-none");
        this.dinAlert.textContent = "DIN not found. Enter inmate details below.";

        // Remove red border from DIN field
        this.dinInput.style.setProperty("border-color", "#545252", "important");

        // Show additional fields
        this.additionalFields.classList.remove("display-none");

        // Enable manual entry mode
        this.isManualEntry = true;
        this.enableAllFields();
        this.wrapper.classList.add("manual");

        // Hide the manual entry button controls (not needed anymore)
        this.manualEntryControls.classList.add("display-none");

        // Initialize facility name autocomplete
        this.initializeFacilityAutocomplete();

        // Initialize Google Places Autocomplete for facility address
        this.initializeAutocomplete();

        // Focus on name field for smooth UX
        this.inmateNameInput.focus();
      }

      handleDINChange() {
        // Reset verification if DIN changes after successful verification
        if (this.verificationData || this.isManualEntry) {
          this.resetVerification();
        }

        // Hide manual entry controls when DIN changes (force re-verification)
        if (this.manualEntryControls) {
          this.manualEntryControls.classList.add("display-none");
        }
      }

      // API Cache Management
      getCachedResponse(din) {
        const cached = this.apiCache.get(din);
        if (!cached) return null;

        // Cache expires after 5 minutes to prevent stale data
        const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
        const isExpired = Date.now() - cached.timestamp > CACHE_DURATION;

        if (isExpired) {
          this.apiCache.delete(din);
          return null;
        }

        return cached.response;
      }

      setCachedResponse(din, response) {
        this.apiCache.set(din, {
          response: response,
          timestamp: Date.now(),
        });
      }

      invalidateCache(din) {
        // Remove specific DIN from cache (used after creating new record)
        if (din) {
          this.apiCache.delete(din.toUpperCase());
        } else {
          // Clear entire cache
          this.apiCache.clear();
        }
      }

      // Real-time error clearing handlers
      handleNameInput() {
        const value = this.inmateNameInput.value.trim();
        if (value) {
          // Clear error as soon as there's any content
          this.fullnameError.classList.add("display-none");
          this.inmateNameInput.style.setProperty(
            "border-color",
            "#545252",
            "important"
          );

          // Additional check for DOCCS format
          if (this.isValidDOCCSFormat(value)) {
            // Extra confirmation it's valid
            this.fullnameError.classList.add("display-none");
          }
        }
      }

      handleFacilityInputChange() {
        const value = this.facilityInput.value.trim();
        if (value) {
          // Clear error immediately when facility name is provided
          this.facilityError.classList.add("display-none");
          this.facilityInput.style.setProperty(
            "border-color",
            "#545252",
            "important"
          );
        }
      }

      handleAddressInput() {
        const value = this.facilityAddressInput.value.trim();
        if (value) {
          // Clear error immediately when address is provided
          this.addressError.classList.add("display-none");
          this.facilityAddressInput.style.setProperty(
            "border-color",
            "#545252",
            "important"
          );
        }
      }

      handleCityInput() {
        const value = this.cityInput.value.trim();
        if (value) {
          // Clear error immediately when city is provided
          this.cityError.classList.add("display-none");
          this.cityInput.style.setProperty(
            "border-color",
            "#545252",
            "important"
          );
        }
      }

      handleStateInput() {
        const value = this.stateInput.value.trim();
        if (value) {
          // Clear error as soon as there's content (don't wait for 2 letters)
          this.stateError.classList.add("display-none");
          this.stateInput.style.setProperty(
            "border-color",
            "#545252",
            "important"
          );
        }
      }

      handleZipInput() {
        const value = this.zipInput.value.trim();
        if (value) {
          // Clear error as soon as there's content (don't wait for perfect format)
          this.zipError.classList.add("display-none");
          this.zipInput.style.setProperty(
            "border-color",
            "#545252",
            "important"
          );
        }
      }

      initializeCheckoutProtection() {
        // Find checkout button - it's outside our component in the main cart
        const checkoutBtn = document.querySelector("#checkout-submit");
        if (checkoutBtn) {
          // Prevent form submission by intercepting both click and form submit
          checkoutBtn.addEventListener(
            "click",
            this.handleCheckoutClick.bind(this)
          );

          // Also intercept the form submission
          const cartForm = document.querySelector("form#cart");
          if (cartForm) {
            cartForm.addEventListener(
              "submit",
              this.handleFormSubmit.bind(this)
            );
          }
        } else {
          // Fallback: try to find any button with name="checkout"
          const fallbackBtn = document.querySelector('button[name="checkout"]');
          if (fallbackBtn) {
            fallbackBtn.addEventListener(
              "click",
              this.handleCheckoutClick.bind(this)
            );
          }
        }
      }

      async handleFormSubmit(e) {
        // Prevent the default form submission
        e.preventDefault();
        e.stopPropagation();

        // Call our checkout handler
        await this.handleCheckoutClick(e);
      }

      async handleCheckoutClick(e) {
        e.preventDefault();
        e.stopPropagation();

        this.setCheckoutButtonLoading(true);

        try {
          if (this.isVerified()) {
            if (!this.validateAutomaticEntryFields()) {
              this.setCheckoutButtonLoading(false);
              return false;
            }
            await this.proceedToCheckout();
            return false;
          }

          if (this.isManualEntry) {
            if (!this.validateManualEntryFields()) {
              this.setCheckoutButtonLoading(false);
              return false;
            }

            try {
              await this.createRecord();
              await this.proceedToCheckout();
            } catch (error) {
              if (
                error.message &&
                (error.message.includes("already exists") ||
                  error.message.includes("duplicate"))
              ) {
                this.showRecordUpdateError();
              } else {
                this.showCheckoutError("Failed to save information");
              }
              this.setCheckoutButtonLoading(false);
            }
            return false;
          }

          this.showCheckoutError();
          this.setCheckoutButtonLoading(false);
          return false;
        } catch (error) {
          this.showCheckoutError("Failed to proceed to checkout");
          this.setCheckoutButtonLoading(false);
        }
      }

      setCheckoutButtonLoading(isLoading) {
        const checkoutBtn = document.querySelector("#checkout-submit");
        if (!checkoutBtn) return;

        if (isLoading) {
          // Store original content if not already stored
          if (!checkoutBtn.dataset.originalContent) {
            checkoutBtn.dataset.originalContent = checkoutBtn.innerHTML;
          }

          // Show loading state
          checkoutBtn.disabled = true;
          checkoutBtn.style.pointerEvents = "none";
          checkoutBtn.style.opacity = "0.7";
          // Add CSS animation if it doesn't exist
          if (!document.getElementById("checkout-loading-css")) {
            const style = document.createElement("style");
            style.id = "checkout-loading-css";
            style.textContent = `
              @keyframes checkout-spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `;
            document.head.appendChild(style);
          }

          checkoutBtn.innerHTML = `
            <span style="display: flex; align-items: center; justify-content: center; gap: 8px;">
              <svg style="width: 16px; height: 16px; animation: checkout-spin 1s linear infinite;" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="60" stroke-dashoffset="60">
                  <animate attributeName="stroke-dashoffset" dur="1.5s" values="60;0;-60" repeatCount="indefinite"/>
                </circle>
              </svg>
              Processing...
            </span>
          `;
        } else {
          // Restore original state
          checkoutBtn.disabled = false;
          checkoutBtn.style.pointerEvents = "";
          checkoutBtn.style.opacity = "";
          if (checkoutBtn.dataset.originalContent) {
            checkoutBtn.innerHTML = checkoutBtn.dataset.originalContent;
          }
        }
      }

      resetCheckoutButtonState() {
        // Reset checkout button loading state when component loads
        // This fixes the issue where the button stays in "Processing..." state
        // when user navigates back from checkout page
        this.setCheckoutButtonLoading(false);
      }

      // Helper methods for validation and state management
      isVerified() {
        return this.verificationData && this.verificationData.success;
      }

      hasCompleteManualData() {
        return (
          this.isManualEntry &&
          this.dinInput.value.trim() &&
          this.inmateNameInput.value.trim() &&
          this.facilityInput.value.trim() &&
          this.facilityAddressInput.value.trim() &&
          this.cityInput.value.trim() &&
          this.stateInput.value.trim() &&
          this.zipInput.value.trim()
        );
      }

      canProceedToCheckout() {
        return this.isVerified() || this.hasCompleteManualData();
      }

      validateManualEntryFields() {
        this.hideAllErrors();
        let hasErrors = false;

        // Validate DIN
        if (!this.dinInput.value.trim()) {
          this.dinInputAlert.textContent = "DIN is required";
          this.dinInputAlert.classList.remove("display-none");
          hasErrors = true;
        } else if (!this.dinRegex.test(this.dinInput.value.toUpperCase())) {
          this.dinInputAlert.textContent =
            "DIN must be in format 00X0000 (e.g., 47B8293)";
          this.dinInputAlert.classList.remove("display-none");
          hasErrors = true;
        }

        // Validate Full Name
        if (!this.inmateNameInput.value.trim()) {
          this.fullnameError.textContent = "Full name is required";
          this.fullnameError.classList.remove("display-none");
          hasErrors = true;
        } else if (!this.isValidDOCCSFormat(this.inmateNameInput.value)) {
          this.fullnameError.textContent =
            "Full name must be in DOCCS format (LAST, FIRST MIDDLE)";
          this.fullnameError.classList.remove("display-none");
          hasErrors = true;
        }

        // Validate Facility Name
        if (!this.facilityInput.value.trim()) {
          this.facilityError.classList.remove("display-none");
          hasErrors = true;
        }

        // Validate Facility Address
        if (!this.facilityAddressInput.value.trim()) {
          this.addressError.classList.remove("display-none");
          hasErrors = true;
        }

        // Validate City
        if (!this.cityInput.value.trim()) {
          this.cityError.classList.remove("display-none");
          hasErrors = true;
        }

        // Validate State
        if (!this.stateInput.value.trim()) {
          this.stateError.classList.remove("display-none");
          hasErrors = true;
        } else if (this.stateInput.value.length !== 2) {
          this.stateError.textContent = "State must be 2 letters (e.g., NY)";
          this.stateError.classList.remove("display-none");
          hasErrors = true;
        }

        // Validate ZIP
        if (!this.zipInput.value.trim()) {
          this.zipError.classList.remove("display-none");
          hasErrors = true;
        } else if (!/^\d{5}(-\d{4})?$/.test(this.zipInput.value)) {
          this.zipError.textContent = "ZIP code must be 5 digits (e.g., 12345)";
          this.zipError.classList.remove("display-none");
          hasErrors = true;
        }

        if (hasErrors) {
          this.wrapper.classList.add("invalid-search");
        }

        return !hasErrors; // Return true if no errors
      }

      // Add validation for automatic entry (pre-filled fields) to prevent HTML manipulation
      validateAutomaticEntryFields() {
        this.hideAllErrors();
        let hasErrors = false;

        // Validate that all required fields have values (could be tampered with via HTML)
        if (!this.dinInput.value.trim()) {
          this.dinInputAlert.textContent = "DIN is required";
          this.dinInputAlert.classList.remove("display-none");
          hasErrors = true;
        }

        if (!this.inmateNameInput.value.trim()) {
          this.fullnameError.textContent = "Full name is required";
          this.fullnameError.classList.remove("display-none");
          hasErrors = true;
        }

        if (!this.facilityInput.value.trim()) {
          this.facilityError.classList.remove("display-none");
          hasErrors = true;
        }

        if (!this.facilityAddressInput.value.trim()) {
          this.addressError.classList.remove("display-none");
          hasErrors = true;
        }

        if (!this.cityInput.value.trim()) {
          this.cityError.classList.remove("display-none");
          hasErrors = true;
        }

        if (!this.stateInput.value.trim()) {
          this.stateError.classList.remove("display-none");
          hasErrors = true;
        }

        if (!this.zipInput.value.trim()) {
          this.zipError.classList.remove("display-none");
          hasErrors = true;
        }

        if (hasErrors) {
          this.wrapper.classList.add("invalid-search");
        }

        return !hasErrors; // Return true if no errors
      }

      isValidDOCCSFormat(name) {
        // DOCCS format: LAST, FIRST MIDDLE or LAST, FIRST
        const doccsPattern = /^[A-Z]+,\s[A-Z]+(\s[A-Z]+)*$/i;
        return doccsPattern.test(name.trim());
      }

      // UI state management methods
      populateFields(recipient) {
        // Show additional fields
        this.additionalFields.classList.remove("display-none");

        // Populate all fields from API response
        this.inmateNameInput.value = recipient.fullName || "";
        this.facilityInput.value = recipient.facilityName || "";
        this.facilityAddressInput.value = recipient.facilityAddress1 || "";
        this.cityInput.value = recipient.city || "";
        this.stateInput.value = recipient.state || "";
        this.zipInput.value = recipient.zip || "";

        // Keep all fields disabled (verified data)
        this.disableAllFields();
      }

      enableAllFields() {
        this.inmateNameInput.disabled = false;
        this.facilityInput.disabled = false;
        this.facilityAddressInput.disabled = false;
        this.cityInput.disabled = false;
        this.stateInput.disabled = false;
        this.zipInput.disabled = false;
      }

      disableAllFields() {
        this.inmateNameInput.disabled = true;
        this.facilityInput.disabled = true;
        this.facilityAddressInput.disabled = true;
        this.cityInput.disabled = true;
        this.stateInput.disabled = true;
        this.zipInput.disabled = true;
      }

      showSuccessState() {
        this.wrapper.classList.add("searched");
        this.wrapper.classList.remove("invalid-search", "manual");

        // Hide errors first
        this.hideAllErrors();

        // Hide manual entry controls and show "data incorrect" message for verified records
        this.enableManualText.style.display = "none";
        this.enableManualBtn.style.display = "none";
        this.dataIncorrectMessage.classList.remove("display-none");
      }

      showNotFoundState(errorMessage = "Record not found in our system") {
        // DO NOT add invalid-search class - no error styling
        this.wrapper.classList.remove("searched");

        // Show informational message instead of error
        this.dinAlert.classList.remove("display-none");
        this.dinAlert.textContent = "DIN not found. Enter inmate details below.";

        // Automatically trigger manual entry without showing button
        this.autoOpenManualEntry();
      }

      showAPIError() {
        this.wrapper.classList.add("invalid-search");
        this.dinAlert.classList.remove("display-none");
        this.dinAlert.textContent = "Service unavailable. Please try again.";
      }

      resetVerification() {
        this.verificationData = null;
        this.isManualEntry = false;

        // Clear all fields
        this.inmateNameInput.value = "";
        this.facilityInput.value = "";
        this.facilityAddressInput.value = "";
        this.cityInput.value = "";
        this.stateInput.value = "";
        this.zipInput.value = "";

        // Clean up autocomplete
        if (this.autocomplete) {
          google.maps.event.clearInstanceListeners(this.facilityAddressInput);
          this.autocomplete = null;
        }

        // Clean up facility autocomplete
        if (this.facilityInput) {
          this.facilityInput.style.backgroundColor = "#E2E1D1"; // Reset to normal color

          // Remove the custom dropdown
          const dropdown = document.querySelector("#facility-dropdown");
          if (dropdown) {
            dropdown.remove();
          }

          this.facilityDropdown = null;
          this.selectedFacilityIndex = -1;
        }

        // Hide additional fields
        this.additionalFields.classList.add("display-none");

        // Reset UI state
        this.disableAllFields();
        this.wrapper.classList.remove("searched", "manual", "invalid-search");
        this.enableManualText.textContent = "Cannot find details?";
        this.enableManualText.style.display = "block";
        this.enableManualBtn.textContent = "Click here to enter manually";
        this.enableManualBtn.style.display = "inline";

        // Hide manual entry controls (force re-verification)
        if (this.manualEntryControls) {
          this.manualEntryControls.classList.add("display-none");
        }

        this.hideAllErrors();
      }

      // Error display methods
      showDINError() {
        this.dinInputAlert.classList.remove("display-none");
        this.wrapper.classList.add("invalid-search");
      }

      showCheckoutError(
        message = "Please click the Verify button before checkout"
      ) {
        this.verifyWarning.textContent = message;
        this.verifyWarning.classList.remove("display-none");
        this.verifyWarning.classList.add("show");
        this.verifyBtn.classList.add("highlight");

        // Remove highlight after 5 seconds
        setTimeout(() => {
          this.verifyBtn.classList.remove("highlight");
        }, 5000);
      }

      showRecordUpdateError() {
        this.recordUpdateError.classList.remove("display-none");
        this.wrapper.classList.add("invalid-search");
      }

      showVerificationRequiredError() {
        this.verificationRequiredError.classList.remove("display-none");
        this.wrapper.classList.add("invalid-search");
      }

      hideAllErrors() {
        // DIN and general errors
        this.dinInputAlert.classList.add("display-none");
        this.dinAlert.classList.add("display-none");
        this.notInCustodyAlert.classList.add("display-none");
        this.verifyWarning.classList.add("display-none");
        this.verifyWarning.classList.remove("show");
        this.verifyBtn.classList.remove("highlight");
        this.recordUpdateError.classList.add("display-none");
        this.dataIncorrectMessage.classList.add("display-none");
        this.verificationRequiredError.classList.add("display-none");

        // Field-specific errors
        this.fullnameError.classList.add("display-none");
        this.facilityError.classList.add("display-none");
        this.addressError.classList.add("display-none");
        this.cityError.classList.add("display-none");
        this.stateError.classList.add("display-none");
        this.zipError.classList.add("display-none");

        this.wrapper.classList.remove("invalid-search");
      }

      // API methods
      async createRecord() {
        const formData = new FormData();
        formData.append("din", this.dinInput.value.toUpperCase());
        formData.append("fullName", this.inmateNameInput.value);
        formData.append("facilityName", this.facilityInput.value);
        formData.append("facilityAddress1", this.facilityAddressInput.value);
        formData.append("city", this.cityInput.value);
        formData.append("state", this.stateInput.value);
        formData.append("zip", this.zipInput.value);
        formData.append("country", this.countryInput.value);

        const response = await fetch(
          `${this.apiBaseUrl}/create?shop=${this.shopDomain}`,
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await response.json();

        if (data.success) {
          this.verificationData = data;
          return data;
        } else {
          throw new Error(data.error || "Failed to create record");
        }
      }

      async proceedToCheckout() {
        try {
          await this.addDataToCartItems();

          // Build checkout URL with pre-filled shipping address
          const checkoutUrl = this.buildCheckoutUrl();
          window.location.href = checkoutUrl;
        } catch (error) {
          this.showCheckoutError("Failed to proceed to checkout");
        }
      }

      buildCheckoutUrl() {
        // Parse the full name (DOCCS format: "LAST, FIRST MIDDLE")
        const fullName = this.inmateNameInput.value.trim();
        let firstName = "";
        let lastName = "";

        if (fullName.includes(",")) {
          const parts = fullName.split(",");
          lastName = parts[0].trim();
          const firstAndMiddle = parts[1].trim();
          // Take only the first name, ignore middle names
          firstName = firstAndMiddle.split(" ")[0];
        }

        // Build the query parameters object
        const params = {
          "checkout[shipping_address][first_name]": firstName,
          "checkout[shipping_address][last_name]": lastName,
          "checkout[shipping_address][address1]":
            this.facilityAddressInput.value,
          "checkout[shipping_address][city]": this.cityInput.value,
          "checkout[shipping_address][province]": this.getFullStateName(
            this.stateInput.value
          ),
          "checkout[shipping_address][province_code]":
            this.stateInput.value.toUpperCase(),
          "checkout[shipping_address][country]": "United States",
          "checkout[shipping_address][country_code]": "US",
          "checkout[shipping_address][zip]": this.zipInput.value,
        };

        // Convert to URL-encoded query string
        const queryString = Object.entries(params)
          .filter(([key, value]) => value) // Only include non-empty values
          .map(
            ([key, value]) =>
              `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
          )
          .join("&");

        return `/checkout?${queryString}`;
      }

      getFullStateName(stateCode) {
        const stateMap = {
          AL: "Alabama",
          AK: "Alaska",
          AZ: "Arizona",
          AR: "Arkansas",
          CA: "California",
          CO: "Colorado",
          CT: "Connecticut",
          DE: "Delaware",
          FL: "Florida",
          GA: "Georgia",
          HI: "Hawaii",
          ID: "Idaho",
          IL: "Illinois",
          IN: "Indiana",
          IA: "Iowa",
          KS: "Kansas",
          KY: "Kentucky",
          LA: "Louisiana",
          ME: "Maine",
          MD: "Maryland",
          MA: "Massachusetts",
          MI: "Michigan",
          MN: "Minnesota",
          MS: "Mississippi",
          MO: "Missouri",
          MT: "Montana",
          NE: "Nebraska",
          NV: "Nevada",
          NH: "New Hampshire",
          NJ: "New Jersey",
          NM: "New Mexico",
          NY: "New York",
          NC: "North Carolina",
          ND: "North Dakota",
          OH: "Ohio",
          OK: "Oklahoma",
          OR: "Oregon",
          PA: "Pennsylvania",
          RI: "Rhode Island",
          SC: "South Carolina",
          SD: "South Dakota",
          TN: "Tennessee",
          TX: "Texas",
          UT: "Utah",
          VT: "Vermont",
          VA: "Virginia",
          WA: "Washington",
          WV: "West Virginia",
          WI: "Wisconsin",
          WY: "Wyoming",
        };

        return stateMap[stateCode.toUpperCase()] || stateCode;
      }

      async addDataToCartItems() {
        try {
          // Get current cart
          const cartResponse = await fetch("/cart.js");
          if (!cartResponse.ok) {
            throw new Error(`Failed to get cart: ${cartResponse.status}`);
          }
          const cart = await cartResponse.json();

          // Get weight information from cart
          const cartWeightInfo = document.querySelector("#cart-weight");
          let foodWeight = "";
          let totalWeight = "";

          if (cartWeightInfo) {
            const weightRows =
              cartWeightInfo.querySelectorAll(".cart-weight-row");
            weightRows.forEach((row) => {
              const label = row.querySelector(".cart-weight-label");
              const value = row.querySelector(".cart-weight-value");

              if (label && value) {
                const labelText = label.textContent.trim().toLowerCase();
                if (labelText.includes("food weight")) {
                  foodWeight = value.textContent.trim(); // Takes exact value with units
                } else if (labelText.includes("total weight")) {
                  totalWeight = value.textContent.trim(); // Takes exact value with units
                }
              }
            });
          }

          const deliveryProperties = {
            DIN: this.dinInput.value.toUpperCase(),
            Name: this.inmateNameInput.value,
            Facility: this.facilityInput.value,
            Address: `${this.facilityAddressInput.value}, ${this.cityInput.value}, ${this.stateInput.value} ${this.zipInput.value}`,
            "Food Weight": foodWeight,
            "Total Weight": totalWeight,
          };

          for (let i = 0; i < cart.items.length; i++) {
            const lineItem = cart.items[i];

            const updatedProperties = {
              ...lineItem.properties,
              ...deliveryProperties,
            };

            const requestData = {
              line: i + 1,
              quantity: lineItem.quantity,
              properties: updatedProperties,
            };

            // Update this line item
            const updateResponse = await fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(requestData),
            });

            if (!updateResponse.ok) {
              throw new Error(
                `Failed to update line item ${i + 1}: ${updateResponse.status}`
              );
            }
          }
        } catch (error) {
          throw error;
        }
      }

      // Google Places Autocomplete methods
      initializeAutocomplete() {
        // Only initialize if Google Places is available and we're in manual mode
        if (
          typeof google === "undefined" ||
          !google.maps ||
          !google.maps.places
        ) {
          return;
        }

        // Initialize autocomplete on facility address field
        this.autocomplete = new google.maps.places.Autocomplete(
          this.facilityAddressInput,
          {
            componentRestrictions: { country: ["us"] }, // US only for correctional facilities
            fields: ["address_components", "geometry", "formatted_address"],
            types: ["establishment", "geocode"], // Allow both establishments and general addresses
          }
        );

        // Listen for place selection
        this.autocomplete.addListener("place_changed", () => {
          this.fillAddressFromPlace();
        });
      }

      initializeFacilityAutocomplete() {
        // Only initialize if facility addresses data is available
        if (!window.facilityAddresses || !this.facilityInput) {
          return;
        }

        // Create facility name suggestions
        this.facilityNames = Object.keys(window.facilityAddresses);

        // Create custom dropdown container
        this.createFacilityDropdown();

        // Listen for facility input changes
        this.facilityInput.addEventListener("input", (e) => {
          this.handleFacilityInput(e.target.value);
        });

        // Listen for focus to show dropdown
        this.facilityInput.addEventListener("focus", () => {
          this.showFacilityDropdown();
        });

        // Listen for blur to hide dropdown (with delay for clicks)
        this.facilityInput.addEventListener("blur", () => {
          setTimeout(() => this.hideFacilityDropdown(), 150);
        });

        // Listen for keyboard navigation
        this.facilityInput.addEventListener("keydown", (e) => {
          this.handleFacilityKeydown(e);
        });
      }

      createFacilityDropdown() {
        // Remove existing dropdown if any
        const existingDropdown = document.querySelector("#facility-dropdown");
        if (existingDropdown) {
          existingDropdown.remove();
        }

        // Create dropdown container
        this.facilityDropdown = document.createElement("div");
        this.facilityDropdown.id = "facility-dropdown";
        this.facilityDropdown.className = "facility-autocomplete-dropdown";
        this.facilityDropdown.style.display = "none";

        // Position dropdown below the input
        this.facilityInput.parentNode.style.position = "relative";
        this.facilityInput.parentNode.appendChild(this.facilityDropdown);

        this.selectedFacilityIndex = -1;
      }

      showFacilityDropdown() {
        if (!this.facilityDropdown || !this.facilityNames) return;

        const query = this.facilityInput.value.toLowerCase().trim();
        const filteredFacilities = this.facilityNames.filter((name) =>
          name.toLowerCase().includes(query)
        );

        if (filteredFacilities.length === 0) {
          this.hideFacilityDropdown();
          return;
        }

        // Clear existing options
        this.facilityDropdown.innerHTML = "";

        // Add filtered facilities to dropdown
        filteredFacilities.forEach((facilityName, index) => {
          const option = document.createElement("div");
          option.className = "facility-option";
          option.textContent = facilityName;
          option.dataset.index = index;

          // Highlight matching text
          if (query) {
            const regex = new RegExp(`(${query})`, "gi");
            option.innerHTML = facilityName.replace(
              regex,
              "<strong>$1</strong>"
            );
          }

          // Add click handler
          option.addEventListener("mousedown", (e) => {
            e.preventDefault(); // Prevent input blur
            this.selectFacility(facilityName);
          });

          // Add hover handler
          option.addEventListener("mouseenter", () => {
            this.highlightFacilityOption(index);
          });

          this.facilityDropdown.appendChild(option);
        });

        // Show dropdown
        this.facilityDropdown.style.display = "block";
        this.selectedFacilityIndex = -1;
      }

      hideFacilityDropdown() {
        if (this.facilityDropdown) {
          this.facilityDropdown.style.display = "none";
        }
        this.selectedFacilityIndex = -1;
      }

      handleFacilityInput(value) {
        // Show dropdown with filtered results
        this.showFacilityDropdown();

        // Handle facility selection logic
        this.handleFacilitySelection();
      }

      handleFacilityKeydown(e) {
        if (
          !this.facilityDropdown ||
          this.facilityDropdown.style.display === "none"
        ) {
          return;
        }

        const options =
          this.facilityDropdown.querySelectorAll(".facility-option");

        switch (e.key) {
          case "ArrowDown":
            e.preventDefault();
            this.selectedFacilityIndex = Math.min(
              this.selectedFacilityIndex + 1,
              options.length - 1
            );
            this.highlightFacilityOption(this.selectedFacilityIndex);
            break;

          case "ArrowUp":
            e.preventDefault();
            this.selectedFacilityIndex = Math.max(
              this.selectedFacilityIndex - 1,
              -1
            );
            this.highlightFacilityOption(this.selectedFacilityIndex);
            break;

          case "Enter":
            e.preventDefault();
            if (
              this.selectedFacilityIndex >= 0 &&
              options[this.selectedFacilityIndex]
            ) {
              const facilityName =
                options[this.selectedFacilityIndex].textContent;
              this.selectFacility(facilityName);
            }
            break;

          case "Escape":
            this.hideFacilityDropdown();
            this.facilityInput.blur();
            break;
        }
      }

      highlightFacilityOption(index) {
        const options =
          this.facilityDropdown.querySelectorAll(".facility-option");

        // Remove previous highlight
        options.forEach((option) => option.classList.remove("highlighted"));

        // Highlight selected option
        if (index >= 0 && options[index]) {
          options[index].classList.add("highlighted");
          this.selectedFacilityIndex = index;
        }
      }

      selectFacility(facilityName) {
        this.facilityInput.value = facilityName;
        this.hideFacilityDropdown();
        this.handleFacilitySelection();

        // Focus next field for better UX
        if (this.facilityAddressInput) {
          setTimeout(() => this.facilityAddressInput.focus(), 100);
        }
      }

      handleFacilitySelection() {
        const selectedFacility = this.facilityInput.value.trim();

        // Check if the selected facility exists in our data
        if (
          window.facilityAddresses &&
          window.facilityAddresses[selectedFacility]
        ) {
          const facilityData = window.facilityAddresses[selectedFacility];

          // Auto-populate address fields from facility data
          this.facilityAddressInput.value = facilityData.address;
          this.cityInput.value = facilityData.city;
          this.stateInput.value = facilityData.state;
          this.zipInput.value = facilityData.zip;
          this.countryInput.value = "US";

          // Clear any previous address errors since we have valid facility data
          this.addressError.classList.add("display-none");
          this.cityError.classList.add("display-none");
          this.stateError.classList.add("display-none");
          this.zipError.classList.add("display-none");
          this.facilityError.classList.add("display-none");

          // Reset border colors to normal for all populated fields
          this.facilityInput.style.setProperty(
            "border-color",
            "#545252",
            "important"
          );
          this.facilityAddressInput.style.setProperty(
            "border-color",
            "#545252",
            "important"
          );
          this.cityInput.style.setProperty(
            "border-color",
            "#545252",
            "important"
          );
          this.stateInput.style.setProperty(
            "border-color",
            "#545252",
            "important"
          );
          this.zipInput.style.setProperty(
            "border-color",
            "#545252",
            "important"
          );

          // Disable address fields since they're auto-populated from known facility
          this.facilityAddressInput.disabled = true;
          this.cityInput.disabled = true;
          this.stateInput.disabled = true;
          this.zipInput.disabled = true;

          // Add visual indication that this is a known facility
          this.facilityInput.style.backgroundColor = "#d1fae5"; // Light green
        } else {
          // Unknown facility - enable address fields for manual entry
          this.facilityAddressInput.disabled = false;
          this.cityInput.disabled = false;
          this.stateInput.disabled = false;
          this.zipInput.disabled = false;

          // Reset background color
          this.facilityInput.style.backgroundColor = "#E2E1D1";

          // Clear auto-populated fields if user is typing a custom facility
          if (
            selectedFacility &&
            !Object.keys(window.facilityAddresses).some((name) =>
              name.toLowerCase().startsWith(selectedFacility.toLowerCase())
            )
          ) {
            // Only clear if this doesn't look like the start of a known facility name
            this.facilityAddressInput.value = "";
            this.cityInput.value = "";
            this.stateInput.value = "";
            this.zipInput.value = "";
          }

          // Re-initialize Google Places autocomplete for custom facility address
          if (
            this.facilityAddressInput &&
            !this.facilityAddressInput.disabled
          ) {
            this.initializeAutocomplete();
          }
        }
      }

      fillAddressFromPlace() {
        const place = this.autocomplete.getPlace();

        if (!place.address_components) {
          return;
        }

        // Reset address fields before filling
        this.cityInput.value = "";
        this.stateInput.value = "";
        this.zipInput.value = "";

        // Variables to build the street address
        let streetNumber = "";
        let route = "";

        // Parse Google's address components
        for (const component of place.address_components) {
          const componentType = component.types[0];

          switch (componentType) {
            case "street_number": // Street number (e.g., "123")
              streetNumber = component.long_name;
              break;
            case "route": // Street name (e.g., "Main Street")
              route = component.long_name;
              break;
            case "locality": // City
              this.cityInput.value = component.long_name;
              break;
            case "administrative_area_level_1": // State
              this.stateInput.value = component.short_name; // Use short name (NY, CA, etc.)
              break;
            case "postal_code": // ZIP code
              this.zipInput.value = component.long_name;
              break;
            case "postal_code_suffix": // ZIP+4 suffix
              if (this.zipInput.value) {
                this.zipInput.value += "-" + component.long_name;
              }
              break;
          }
        }

        // Build the complete street address
        if (streetNumber && route) {
          // Perfect: We have both street number and street name
          this.facilityAddressInput.value = `${streetNumber} ${route}`;
          // Clear any previous address errors and reset border
          this.addressError.classList.add("display-none");
          this.facilityAddressInput.style.setProperty(
            "border-color",
            "#545252",
            "important"
          );
        } else if (route) {
          // Good: We have at least a street name
          this.facilityAddressInput.value = route;
          // Clear any previous address errors and reset border
          this.addressError.classList.add("display-none");
          this.facilityAddressInput.style.setProperty(
            "border-color",
            "#545252",
            "important"
          );
        } else {
          // Problem: No street components found - this is likely a city-only result
          // Don't put city name in address field, leave it empty and show error
          this.facilityAddressInput.value = "";

          // Show specific error for city-only selections
          this.addressError.textContent =
            "Please enter a specific street address, not just a city name";
          this.addressError.classList.remove("display-none");
        }

        // Clear errors and reset borders for populated fields
        if (this.cityInput.value.trim()) {
          this.cityError.classList.add("display-none");
          this.cityInput.style.setProperty(
            "border-color",
            "#545252",
            "important"
          );
        }

        if (this.stateInput.value.trim()) {
          this.stateError.classList.add("display-none");
          this.stateInput.style.setProperty(
            "border-color",
            "#545252",
            "important"
          );
        }

        if (this.zipInput.value.trim()) {
          this.zipError.classList.add("display-none");
          this.zipInput.style.setProperty(
            "border-color",
            "#545252",
            "important"
          );
        }

        // Validate the filled address
        this.validateFilledAddress();
      }

      async createRecord() {
        const formData = new FormData();
        formData.append("din", this.dinInput.value.toUpperCase());
        formData.append("fullName", this.inmateNameInput.value.trim());
        formData.append("facilityName", this.facilityInput.value.trim());
        formData.append(
          "facilityAddress1",
          this.facilityAddressInput.value.trim()
        );
        formData.append("city", this.cityInput.value.trim());
        formData.append("state", this.stateInput.value.trim());
        formData.append("zip", this.zipInput.value.trim());
        formData.append("country", this.countryInput.value.trim());

        const response = await fetch(
          `${this.apiBaseUrl}/create?shop=${this.shopDomain}`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error || `HTTP ${response.status}: ${response.statusText}`
          );
        }

        const data = await response.json();
        this.verificationData = data; // Store the newly created record as verified

        // Invalidate cache for this DIN since we just created a new record
        this.invalidateCache(this.dinInput.value.toUpperCase());

        return data;
      }

      async proceedToCheckout() {
        try {
          await this.addDataToCartItems();

          // Build checkout URL with pre-filled shipping address
          const checkoutUrl = this.buildCheckoutUrl();
          window.location.href = checkoutUrl;
        } catch (error) {
          this.showCheckoutError("Failed to proceed to checkout");
        }
      }

      buildCheckoutUrl() {
        // Parse the full name (DOCCS format: "LAST, FIRST MIDDLE")
        const fullName = this.inmateNameInput.value.trim();
        let firstName = "";
        let lastName = "";

        if (fullName.includes(",")) {
          const parts = fullName.split(",");
          lastName = parts[0].trim();
          const firstAndMiddle = parts[1].trim();
          // Take only the first name, ignore middle names
          firstName = firstAndMiddle.split(" ")[0];
        }

        // Build the query parameters object
        const params = {
          "checkout[shipping_address][first_name]": firstName,
          "checkout[shipping_address][last_name]": lastName,
          "checkout[shipping_address][address1]":
            this.facilityAddressInput.value,
          "checkout[shipping_address][city]": this.cityInput.value,
          "checkout[shipping_address][province]": this.getFullStateName(
            this.stateInput.value
          ),
          "checkout[shipping_address][province_code]":
            this.stateInput.value.toUpperCase(),
          "checkout[shipping_address][country]": "United States",
          "checkout[shipping_address][country_code]": "US",
          "checkout[shipping_address][zip]": this.zipInput.value,
        };

        // Convert to URL-encoded query string
        const queryString = Object.entries(params)
          .filter(([key, value]) => value) // Only include non-empty values
          .map(
            ([key, value]) =>
              `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
          )
          .join("&");

        return `/checkout?${queryString}`;
      }

      getFullStateName(stateCode) {
        const stateMap = {
          AL: "Alabama",
          AK: "Alaska",
          AZ: "Arizona",
          AR: "Arkansas",
          CA: "California",
          CO: "Colorado",
          CT: "Connecticut",
          DE: "Delaware",
          FL: "Florida",
          GA: "Georgia",
          HI: "Hawaii",
          ID: "Idaho",
          IL: "Illinois",
          IN: "Indiana",
          IA: "Iowa",
          KS: "Kansas",
          KY: "Kentucky",
          LA: "Louisiana",
          ME: "Maine",
          MD: "Maryland",
          MA: "Massachusetts",
          MI: "Michigan",
          MN: "Minnesota",
          MS: "Mississippi",
          MO: "Missouri",
          MT: "Montana",
          NC: "North Carolina",
          NE: "Nebraska",
          NV: "Nevada",
          NH: "New Hampshire",
          NJ: "New Jersey",
          NM: "New Mexico",
          NY: "New York",
          ND: "North Dakota",
          OH: "Ohio",
          OK: "Oklahoma",
          OR: "Oregon",
          PA: "Pennsylvania",
          RI: "Rhode Island",
          SC: "South Carolina",
          SD: "South Dakota",
          TN: "Tennessee",
          TX: "Texas",
          UT: "Utah",
          VT: "Vermont",
          VA: "Virginia",
          WA: "Washington",
          WV: "West Virginia",
          WI: "Wisconsin",
          WY: "Wyoming",
        };
        return stateMap[stateCode.toUpperCase()] || stateCode;
      }

      async addDataToCartItems() {
        try {
          // Get current cart
          const cartResponse = await fetch("/cart.js");
          if (!cartResponse.ok) {
            throw new Error(`Failed to get cart: ${cartResponse.status}`);
          }
          const cart = await cartResponse.json();

          // Get weight information from cart
          const cartWeightInfo = document.querySelector("#cart-weight");
          let foodWeight = "";
          let totalWeight = "";

          if (cartWeightInfo) {
            const weightRows =
              cartWeightInfo.querySelectorAll(".cart-weight-row");
            weightRows.forEach((row) => {
              const label = row.querySelector(".cart-weight-label");
              const value = row.querySelector(".cart-weight-value");

              if (label && value) {
                const labelText = label.textContent.trim().toLowerCase();
                if (labelText.includes("food weight")) {
                  foodWeight = value.textContent.trim(); // Takes exact value with units
                } else if (labelText.includes("total weight")) {
                  totalWeight = value.textContent.trim(); // Takes exact value with units
                }
              }
            });
          }

          const deliveryProperties = {
            DIN: this.dinInput.value.toUpperCase(),
            Name: this.inmateNameInput.value,
            Facility: this.facilityInput.value,
            Address: `${this.facilityAddressInput.value}, ${this.cityInput.value}, ${this.stateInput.value} ${this.zipInput.value}`,
            "Food Weight": foodWeight,
            "Total Weight": totalWeight,
          };

          for (let i = 0; i < cart.items.length; i++) {
            const lineItem = cart.items[i];

            const updatedProperties = {
              ...lineItem.properties,
              ...deliveryProperties,
            };

            const requestData = {
              line: i + 1,
              quantity: lineItem.quantity,
              properties: updatedProperties,
            };

            // Update this line item
            const updateResponse = await fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(requestData),
            });

            if (!updateResponse.ok) {
              throw new Error(
                `Failed to update line item ${i + 1}: ${updateResponse.status}`
              );
            }
          }
        } catch (error) {
          throw error;
        }
      }

      async validateFilledAddress() {
        // Create address string for validation
        const addressString = `${this.facilityAddressInput.value}, ${this.cityInput.value}, ${this.stateInput.value} ${this.zipInput.value}`;

        try {
          const isValid = await this.validateAddressWithGeocoding(
            addressString
          );
          if (!isValid) {
            // Could show a warning to user here
          }
        } catch (error) {}
      }

      async validateAddressWithGeocoding(address) {
        try {
          const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            address
          )}&key=${this.GOOGLE_API_KEY}`;
          const response = await fetch(url);
          const data = await response.json();

          if (data.status === "OK" && data.results.length > 0) {
            return true;
          } else {
            return false;
          }
        } catch (error) {
          return false; // Don't block form submission on API errors
        }
      }
    }
  );
}
